import type { User } from "firebase/auth";
import { authed } from "./referral";
import type { BillingMode } from "./billing";

export type PayMethod = "card" | "ach";

export interface QuoteOption {
  feeCents: number;
  totalCents: number;
}

export interface QuoteDetails {
  name: string | null;
  company: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  /** Two-letter country code. */
  country: string | null;
  eventAt: string | null;
  notes: string | null;
}

export interface Quote {
  id: string;
  invoiceNumber: string | null;
  poNumber: string | null;
  title: string;
  description: string | null;
  /** The real price, before the special discount. */
  listCents: number;
  discountPercent: number;
  /** What the customer owes before any processing fee. */
  netCents: number;
  /** yyyy-MM-dd */
  dueDate: string;
  status: "open" | "paid" | "void";
  paidAt: string | null;
  hasDetails: boolean;
  details: QuoteDetails;
  options: Record<PayMethod, QuoteOption>;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

export interface MyQuotes {
  licensed: boolean;
  quotes: Quote[];
}

export interface PayStart {
  clientSecret: string;
  publishableKey: string;
  mode: BillingMode;
  method: PayMethod;
  netCents: number;
  feeCents: number;
  totalCents: number;
}

/** Throws with the API's own message when it gave one (for example "Enter the full address"), so it can be shown to the person. */
async function json<T>(res: Response, what: string): Promise<T> {
  if (res.ok) return res.json();
  let message = `${what} ${res.status}`;
  try {
    const body = await res.json();
    if (typeof body?.message === "string") message = body.message;
  } catch {
    /* no body */
  }
  throw new Error(message);
}

export const fetchMyQuotes = async (user: User): Promise<MyQuotes> => json(await authed(user, "/quotes/mine"), "quotes/mine");

export const saveQuoteDetails = async (user: User, id: string, details: Partial<Record<keyof QuoteDetails, string>>): Promise<Quote> =>
  json(await authed(user, "/quotes/savedetails", { method: "POST", body: JSON.stringify({ id, ...details }) }), "quotes/savedetails");

export const payQuote = async (user: User, id: string, method: PayMethod): Promise<PayStart> =>
  json(await authed(user, "/quotes/pay", { method: "POST", body: JSON.stringify({ id, method }) }), "quotes/pay");

// ---- admin ----

export interface AccountChoice {
  uid: string;
  email: string;
  name: string | null;
}

export interface NewQuote {
  uid: string;
  email: string;
  invoiceNumber: string;
  poNumber: string;
  title: string;
  description: string;
  /** Dollars, the real price before the discount. */
  amount: number;
  discountPercent: number;
  dueDate: string;
}

export interface AdminQuoteRow {
  mode: BillingMode;
  uid: string;
  email: string | null;
  createdAt: string;
  quote: Quote;
}

export const adminFetchAccounts = async (user: User): Promise<AccountChoice[]> => json(await authed(user, "/quotes/accounts"), "quotes/accounts");

export const adminCreateQuote = async (user: User, quote: NewQuote): Promise<Quote> =>
  json(await authed(user, "/quotes/create", { method: "POST", body: JSON.stringify(quote) }), "quotes/create");

export const adminFetchQuotes = async (user: User): Promise<AdminQuoteRow[]> => json(await authed(user, "/quotes/adminlist"), "quotes/adminlist");

export async function adminVoidQuote(user: User, id: string): Promise<void> {
  const res = await authed(user, "/quotes/void", { method: "POST", body: JSON.stringify({ id }) });
  if (!res.ok) await json(res, "quotes/void");
}

// ---- display ----

export const money = (cents: number) => (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

/** "2030-11-17" as a date in the person's own time zone (parsing it as UTC would show the day before in the Americas). */
export const formatDueDate = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

/** What the customer owes by their choice, with the processing fee for it. */
export const totalFor = (quote: Quote, method: PayMethod) => quote.options[method].totalCents;
